import React, { useEffect, useState } from "react";
import "./index.css";
import { Content } from "antd/es/layout/layout";
import HeaderDocumentAnalysis from "../../assets/img/documentAnalysis.png";
import FileUpload from "../../assets/img/file-upload-icon.png";
import RemoveIcon from "../../assets/img/remove-icon.png";
import {
  Button,
  message,
  Modal,
  Progress,
  Radio,
  Input,
  Form,
  Tooltip,
  Spin,
} from "antd";
import { get, postWithHeaders } from "../../utils/apiUtils";
import useDrivePicker from "react-google-drive-picker";
import { initGoogleDriveClient } from "../../services/googleDriveService";
import { gapi } from "gapi-script";
import {
  HeaderDataType,
  LanguageType,
  ReadabilityType,
} from "../../types/types";
import DocumentModals from "./DocumentsModal";
import FileUploadImage from "../../assets/img/fileUpload.png";
import { parseEntities } from "../../hooks/entitiesForDocsFiles";

const { TextArea } = Input;

function GoogleDrive() {
  const [fileList, setFileList] = useState<any>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false); // For download modal
  const [processedFileURL, setProcessedFileURL] = useState<string>(""); // Store processed file URL
  const [language, setLanguage] = useState("");
  // const [tokenizationType, setTokenizationType] = useState<string>(""); // For tokenization input
  const [stopWordsEnabled, setStopWordsEnabled] = useState<boolean>(false);
  const [supportedLanguages, setSupportedLanguages] = useState<LanguageType[]>(
    []
  );
  const [stemOrLemmatize, setStemOrLemmatize] = useState<string>("");
  const [previewURL, setPreviewURL] = useState<string>("");
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openPicker, authResponse] = useDrivePicker();
  const [summarize, setSummarize] = useState<string>("false");
  const [sentimentAnalysis, setSentimentAnalysis] = useState<string>("false");
  const [enableReadability, setReadability] = useState<string>("false");
  const [enableEntities, setExtractEntities] = useState<string>("false");
  const [enableGrammarCheck, setEnableGrammarCheck] = useState<string>("false");
  const [tokenizationType, setTokenizationType] = useState<string>("false");
  const [headerData, setHeaderData] = useState<HeaderDataType>({
    title: "",
    tokenizationType: "",
    summaryIncluded: "",
    wordCount: "",
    language: "",
    date: "",
    numPages: 0,
    sentiment: "",
    readability: {
      fleschReadingScore: 0,
      gunningFogIndex: 0,
      smogIndex: 0,
    },
    entities: [],
  });

  useEffect(() => {
    if (typeof gapi !== "undefined") {
      initGoogleDriveClient();
    } else {
      console.error("Google API client not loaded.");
    }
  }, []);

  const createPreviewURL = (file: File) => {
    const fileURL = window.URL.createObjectURL(file);
    setPreviewURL(fileURL);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const file = files[0];
      const newFile = {
        uid: file.name,
        name: file.name,
        status: "done",
        originFileObj: file,
      };
      setFileList([newFile]);
      createPreviewURL(file);
      handleModalCancel();
    }
  };

  const handleOpenPreviewModal = () => {
    if (fileList.length === 0) {
      message.error("No file to preview");
      return;
    }

    const selectedFile = fileList[0];

    if (selectedFile.url) {
      // Google Drive file, use its embed URL for preview
      setPreviewURL(
        `https://docs.google.com/document/d/${selectedFile.uid}/preview`
      );
    }

    // Open preview modal
    setIsPreviewModalVisible(true);
  };

  const handlePreviewModalCancel = () => {
    setIsPreviewModalVisible(false);
  };

  const handleProcessClick = async () => {
    setLoading(true);
    const formData = new FormData();

    if (fileList.length === 0) {
      message.error("No file selected");
      setLoading(false);
      return;
    }

    const selectedFile = fileList[0];

    // Kontrollo nëse është file nga Google Drives
    if (selectedFile.url) {
      const googleAuthToken = authResponse?.access_token;

      if (!googleAuthToken) {
        message.error("Failed to get Google Auth token");
        setLoading(false);
        return;
      }

      formData.append("google_drive_file_id", selectedFile.uid);
      formData.append("file", "");
      formData.append("google_auth_code", googleAuthToken);
    } else {
      formData.append("file", selectedFile.originFileObj);
      formData.append("google_drive_file_id", "");
    }

    formData.append("summarize", summarize === "true" ? "true" : "false");
    formData.append(
      "sentiment_analysis",
      sentimentAnalysis === "true" ? "true" : "false"
    );
    formData.append(
      "readability_analysis",
      enableReadability === "true" ? "true" : "false"
    );
    formData.append(
      "entity_extraction",
      enableEntities === "true" ? "true" : "false"
    );
    formData.append(
      "grammar_check",
      enableGrammarCheck === "true" ? "true" : "false"
    );

    formData.append(
      "tokenize",
      tokenizationType === "false" ? "false" : "true"
    );
    formData.append("tokenize_by", tokenizationType);
    formData.append(
      "custom_delim",
      tokenizationType === "custom" ? "your_custom_delim" : ""
    );
    formData.append("stem", stemOrLemmatize === "stemming" ? "true" : "false");
    formData.append(
      "lemmatize",
      stemOrLemmatize === "lemmatization" ? "true" : "false"
    );
    formData.append("remove_stopwords", stopWordsEnabled ? "true" : "false");
    formData.append("language", language);

    try {
      // Thirrja API
      const response = await postWithHeaders("preprocess", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.type === "success") {
        const { data, headers } = response;

        const contentType = headers["content-type"] || "";

        if (
          contentType.includes(
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          )
        ) {
          // If the response is a document file, generate a download link
          const fileURL = window.URL.createObjectURL(new Blob([data]));
          setProcessedFileURL(fileURL);
          setIsDownloadModalVisible(true);
          message.success("File processed successfully");

          const entities = headers["x-entities"];
          const readability = headers["x-readability"];
          const sentiment = headers["x-sentiment"];

          let parsedEntities = [];
          let parsedReadability: ReadabilityType = {
            fleschReadingScore: 0,
            gunningFogIndex: 0,
            smogIndex: 0,
          };

          if (readability) {
            if (readability === "Not available") {
              console.warn(
                "Readability is not available. Setting default values."
              );
            } else if (typeof readability === "string") {
              try {
                const fixedReadability = readability.replace(/'/g, '"');

                const tempReadability = JSON.parse(fixedReadability);

                parsedReadability = {
                  fleschReadingScore: tempReadability.flesch_reading_ease || 0,
                  gunningFogIndex: tempReadability.gunning_fog || 0,
                  smogIndex: tempReadability.smog_index || 0,
                };
              } catch (error) {
                message.error(
                  "Failed to parse readability from server response."
                );
              }
            } else {
              console.warn(
                "Readability header exists but is not a valid string."
              );
            }
          }

          try {
            parsedEntities = parseEntities(entities);
            console.log("Parsed entities:", parsedEntities);
          } catch (error) {
            console.error("Error parsing entities:", error);
            message.error("Failed to parse entities from server response.");
          }
          setHeaderData({
            title: headers["x-title"] || "N/A",
            tokenizationType: headers["x-tokenization-type"] || "N/A",
            summaryIncluded:
              headers["x-summary-included"] === "True" ? "Yes" : "No",
            wordCount: headers["x-word-count"] || "N/A",
            language: headers["x-language"] || "N/A",
            date: headers["x-processed-at"] || "N/A",
            numPages: parseInt(headers["x-num-pages"] || "0", 10),
            sentiment: headers["x-sentiment"] || "UNKNOWN",
            readability: parsedReadability,
            entities: parsedEntities,
          });
          message.success("Data processed successfully!");
        }
      } else {
        // If the server response indicates an error
        message.error("Error processing the document.");
      }
    } catch (error: any) {
      // Handle any unexpected errors
      console.error("API Call Error:", error);
      message.error(`Error processing the document: ${error.message}`);
    } finally {
      setLoading(false); // End the loading state
    }
  };
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleDownloadModalCancel = () => {
    setIsDownloadModalVisible(false);
    setLoading(false);
  };

  useEffect(() => {
    const fetchSupportedLanguages = async () => {
      try {
        const response = await get<any>("languages/", {});
        setSupportedLanguages(response.data as LanguageType[]);
      } catch (error) {
        console.error(
          "There was an error fetching the supported languages:",
          error
        );
      }
    };

    fetchSupportedLanguages();
  }, []);

  const handleOpenPicker = async () => {
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "ID_JUYT";
    const API_KEY = process.env.REACT_APP_API_KEY || "";

    openPicker({
      clientId: CLIENT_ID,
      developerKey: API_KEY,
      viewId: "DOCS",
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: false,
      callbackFunction: (data) => {
        if (data.action === "picked") {
          const selectedGoogleDriveFile = {
            uid: data.docs[0].id,
            name: data.docs[0].name,
            status: "done",
            url: data.docs[0].url,
          };

          setFileList([selectedGoogleDriveFile]);
          handleModalCancel();
        } else {
          console.log("User clicked cancel/close button");
        }
      },
    });
  };

  const handleCancel = () => {
    setFileList([]);
    setTokenizationType("");
    setStopWordsEnabled(false);
    setUploadProgress(0);
    setStemOrLemmatize("");
    setSummarize("");
    setSentimentAnalysis("");
    setReadability("");
    setExtractEntities("");
    setEnableGrammarCheck("");
    setLoading(false);
  };
  return (
    <div className="user-management-layout" style={{ marginLeft: 0 }}>
      <Content className="content-styles">
        <div className="header-image">
          <img
            src={HeaderDocumentAnalysis}
            alt="document analysis"
            style={{ width: "100%" }}
          />
        </div>

        <div className="card">
          <div className="upload-document-style">
            <h2>Upload your document here</h2>
            <div className="upload-file-section">
              <img
                className="img-style"
                src={FileUploadImage}
                alt="upload file"
              />
              <Button onClick={showModal} className="uploadButtonStyle">
                <span
                  style={{ textDecoration: "underline", marginLeft: "3px" }}
                >
                  Click to select files
                </span>
              </Button>
              <span
                style={{
                  color: "#737373",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Word or PDF
              </span>
              <span
                style={{
                  color: "#737373",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Max file size: 5MB
              </span>
            </div>

            <Modal
              title="Choose File Source"
              visible={isModalVisible}
              onCancel={handleModalCancel}
              footer={null}
              className="download-modal-style"
            >
              <div style={{ display: "flex", justifyContent: "space-around" }}>
                <Button
                  onClick={handleOpenPicker}
                  className="drive-files-style"
                >
                  Select files from Drive
                </Button>
                <Button
                  type="default"
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = ".docx";
                    input.onchange = (event: any) => {
                      handleFileUpload(
                        event as React.ChangeEvent<HTMLInputElement>
                      );
                    };
                    input.click();
                  }}
                  className="mycomputer-files-style"
                >
                  Select files from Computer
                </Button>
              </div>
            </Modal>
            {/* Selected Files List */}
            {fileList.length > 0 && (
              <div className="selected-file-list" style={{ marginTop: "20px" }}>
                {fileList.map((file: any) => (
                  <div key={file.uid} className="file-container">
                    <div className="file-inner-container">
                      <img
                        src={FileUpload}
                        alt="test"
                        style={{ height: "44px", width: "44px" }}
                      />

                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{file.size}</span>
                    </div>

                    <img
                      src={RemoveIcon}
                      alt="remove"
                      onClick={() => setFileList([])}
                      style={{ width: "24px", height: "24px" }}
                    />
                  </div>
                ))}
                <Progress percent={uploadProgress} status="active" />
              </div>
            )}

            <Modal
              title="You have two options to choose from for your files"
              visible={isModalVisible}
              onCancel={handleModalCancel}
              footer={null}
              className="download-modal-style"
              style={{ width: "604px" }}
            >
              <p>
                You're about to delete the selected survey permanently. This
                action can't be undone. Are you sure you want to continue?
              </p>
              <div style={{ display: "flex", justifyContent: "space-around" }}>
                <button
                  onClick={() => handleOpenPicker()}
                  className="drive-files-style"
                >
                  Select files from drive
                </button>

                <Button
                  type="default"
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = ".docx";

                    input.onchange = (event: Event) => {
                      const target = event.target as HTMLInputElement;
                      if (target.files && target.files.length > 0) {
                        handleFileUpload({
                          target,
                        } as React.ChangeEvent<HTMLInputElement>);
                      }
                    };

                    input.click(); // This opens the file selection dialog
                    // Keep the modal open to allow multiple selections
                  }}
                  className="mycomputer-files-style"
                >
                  Select files from computer
                </Button>
              </div>
            </Modal>
          </div>

          <div className="main-content">
            <h3>Text Preprocessing Options</h3>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label="Summarize"></Form.Item>

                <div className="general-content">
                  <div className="radio-divv">
                    <Radio
                      checked={summarize === "true"}
                      onClick={() =>
                        setSummarize((prevValue) =>
                          prevValue === "true" ? "" : "true"
                        )
                      }
                    >
                      Summarize
                    </Radio>
                  </div>
                  <div className="radio-divv">
                    <Radio
                      checked={summarize === "false"}
                      onClick={() =>
                        setSummarize((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                    >
                      Do not summarize
                    </Radio>
                  </div>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label="Tokenization"></Form.Item>
                <div>
                  <div
                    className="tokenization-style"
                    // style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Tooltip title="Tokenize by word splits text into individual words, treating each word as a separate token for analysis.">
                      <div className="radio-divv">
                        <Radio
                          checked={tokenizationType === "word"}
                          onClick={() =>
                            setTokenizationType((prevValue) =>
                              prevValue === "word" ? "" : "word"
                            )
                          }
                        >
                          Tokenize by word
                        </Radio>
                      </div>
                    </Tooltip>
                    <Tooltip title="Tokenize by sentence splits text into sentences. Each sentence is treated as a separate token for analysis.">
                      <div className="radio-divv">
                        <Radio
                          checked={tokenizationType === "sentence"}
                          onClick={() =>
                            setTokenizationType((prevValue) =>
                              prevValue === "sentence" ? "" : "sentence"
                            )
                          }
                        >
                          Tokenize by sentence
                        </Radio>
                      </div>
                    </Tooltip>
                    <Tooltip title="Custom delimiter allows you to split text based on a delimiter you define, such as commas or semicolons.">
                      <div className="radio-divv">
                        <Radio
                          checked={tokenizationType === "custom"}
                          onClick={() =>
                            setTokenizationType((prevValue) =>
                              prevValue === "custom" ? "" : "custom"
                            )
                          }
                        >
                          Custom delimiter
                        </Radio>
                      </div>
                    </Tooltip>
                  </div>
                </div>

                {tokenizationType === "custom" && (
                  <div
                    style={{
                      background: "#FCF3FF",
                      padding: "1rem",
                      marginTop: "1rem  ",
                    }}
                  >
                    <h4 className="h4" style={{ marginTop: "unset" }}>
                      Write here your custom delimiter
                    </h4>
                    <TextArea
                      placeholder="Write here..."
                      autoSize={{ minRows: 2, maxRows: 20 }}
                    />{" "}
                  </div>
                )}
              </div>
            </div>

            <div className="tokenization-content">
              <h4 className="h4">Choose stemming or lemmatization</h4>
              <div>
                <div className="general-content">
                  <Tooltip title="Stemming is the process of reducing words to their base form by removing suffixes.">
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={stemOrLemmatize === "stemming"}
                        onClick={() =>
                          setStemOrLemmatize((prevValue) =>
                            prevValue === "stemming" ? "" : "stemming"
                          )
                        }
                      >
                        Stemming
                      </Radio>
                    </div>
                  </Tooltip>
                  <Tooltip title="Lemmatization reduces words to their base form, considering their meaning.">
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={stemOrLemmatize === "lemmatization"}
                        onClick={() =>
                          setStemOrLemmatize((prevValue) =>
                            prevValue === "lemmatization" ? "" : "lemmatization"
                          )
                        }
                      >
                        Lemmatization
                      </Radio>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <h4 className="h4">Stop words removal</h4>
              <div className="radio-divv">
                <Radio
                  checked={stopWordsEnabled}
                  onClick={() => setStopWordsEnabled((prevValue) => !prevValue)}
                >
                  Enable stop word removal
                </Radio>
              </div>

              {stopWordsEnabled && (
                <div
                  style={{
                    background: "#FCF3FF",
                    padding: "1rem",
                    marginTop: "1rem  ",
                  }}
                >
                  <h4 className="h4" style={{ marginTop: "unset" }}>
                    Write here if enabled stop words
                  </h4>
                  <TextArea
                    placeholder="Write here if enabled stop words.."
                    autoSize={{ minRows: 2, maxRows: 20 }}
                  />{" "}
                </div>
              )}
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label="Sentiment analysis"></Form.Item>
                <div
                  className="general-content"
                  // style={{ display: "flex", gap: "2rem" }}
                >
                  <Tooltip title="Enable sentiment analysis to determine the emotional tone of the text.">
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={sentimentAnalysis === "true"}
                        onClick={() =>
                          setSentimentAnalysis((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        Enable sentiment analysis
                      </Radio>
                    </div>
                  </Tooltip>

                  <Tooltip title="Disable sentiment analysis to skip emotional tone evaluation.">
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={sentimentAnalysis === "false"}
                        onClick={() =>
                          setSentimentAnalysis((prevValue) =>
                            prevValue === "false" ? "" : "false"
                          )
                        }
                      >
                        Do not enable sentiment analysis
                      </Radio>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label="Enable readability"></Form.Item>
                <div className="general-content">
                  <Tooltip
                    className="readability-tooltip"
                    title={
                      <div className="readAbility-style-tooltip">
                        <p>
                          <strong>Purpose:</strong> Assesses the readability
                          level of the text, which reflects how easy or
                          difficult the document is to read.
                        </p>
                        <p>
                          <strong>Flesch Reading Ease:</strong>
                        </p>
                        <p>
                          Definition: A metric that assigns a score to a text
                          based on the average sentence length (in words) and
                          the average number of syllables per word.
                        </p>
                        <ul>
                          <li>
                            <strong>90–100:</strong> Very easy to read (e.g.,
                            for 5th-grade students).
                          </li>
                          <li>
                            <strong>60–70:</strong> Standard readability (e.g.,
                            for 8th–9th-grade students).
                          </li>
                          <li>
                            <strong>0–30:</strong> Very difficult to read (e.g.,
                            for college graduates).
                          </li>
                        </ul>
                        <p>
                          <strong>Gunning Fog Index:</strong>
                        </p>
                        <p>
                          Definition: Estimates the years of formal education
                          required to understand the text on the first reading.
                        </p>
                        <ul>
                          <li>
                            <strong>6–10:</strong> Easily understood by the
                            general public.
                          </li>
                          <li>
                            <strong>Above 17:</strong> Very difficult, suitable
                            for professional or academic contexts.
                          </li>
                        </ul>
                        <p>
                          <strong>SMOG Index:</strong>
                        </p>
                        <p>
                          Definition: Estimates the years of education required
                          to understand a piece of writing by focusing on
                          polysyllabic words (words with three or more
                          syllables).
                        </p>
                        <ul>
                          <li>
                            Typically used for texts longer than 30 sentences;
                            it is generally more accurate for texts intended for
                            public consumption, such as brochures or health
                            information.
                          </li>
                        </ul>
                      </div>
                    }
                    placement="right"
                  >
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={enableReadability === "true"}
                        onClick={() =>
                          setReadability((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        Enable readability
                      </Radio>
                    </div>
                  </Tooltip>
                  <div className="radio-divv">
                    <Radio
                      className="custom-radio"
                      checked={enableReadability === "false"}
                      onClick={() =>
                        setReadability((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                    >
                      Do not enable readability
                    </Radio>
                  </div>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label="Extract entities"></Form.Item>
                <div className="general-content">
                  <Tooltip title="Purpose: Identifies key entities in the text, such as people, places, organizations, and dates.">
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={enableEntities === "true"}
                        onClick={() =>
                          setExtractEntities((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        Extract entities
                      </Radio>
                    </div>
                  </Tooltip>
                  <div className="radio-divv">
                    <Radio
                      className="custom-radio"
                      checked={enableEntities === "false"}
                      onClick={() =>
                        setExtractEntities((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                    >
                      Do not extract entities
                    </Radio>
                  </div>
                </div>
              </div>
            </div>
            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label="Grammar check:"></Form.Item>
                <div className="general-content">
                  <Tooltip title="Purpose: Identifies key entities in the text, such as people, places, organizations, and dates.">
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={enableGrammarCheck === "true"}
                        onClick={() =>
                          setEnableGrammarCheck((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        Grammar check
                      </Radio>
                    </div>
                  </Tooltip>
                  <div className="radio-divv">
                    <Radio
                      className="custom-radio"
                      checked={enableGrammarCheck === "false"}
                      onClick={() =>
                        setEnableGrammarCheck((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                    >
                      Do not grammar check
                    </Radio>
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "1rem" }}
            >
              <button onClick={handleCancel} className="cancelButtonStyle">
                Cancel
              </button>
              <button
                onClick={handleProcessClick}
                className="processButtonStyle"
              >
                {loading ? <Spin /> : "Process"}
              </button>{" "}
            </div>
          </div>
        </div>
        <DocumentModals
          isDownloadModalVisible={isDownloadModalVisible}
          isPreviewModalVisible={isPreviewModalVisible}
          handleDownloadModalCancel={handleDownloadModalCancel}
          handlePreviewModalCancel={handlePreviewModalCancel}
          handleOpenPreviewModal={handleOpenPreviewModal}
          processedFileURL={processedFileURL}
          headerData={headerData}
          previewURL={previewURL}
        />
      </Content>
    </div>
  );
}

export default GoogleDrive;
